import { Form, Modal, notification } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "store/slices/modalsSlice";
import {
  getSupportCourse,
  updateSupportCourse,
  getSupportCourses,
} from "store/slices/supportCoursesSlice";
import SupportCourseForm from "./support-course-form";
import { IMAGE_URL } from "constants/ApiConstant";
import { env } from "configs/EnvironmentConfig";

const EditSupportCourseModal = () => {
  const { t } = useTranslation();
  const [form] = Form?.useForm();
  const dispatch = useDispatch();

  const { isModalOpen, modalType, modalProps } = useSelector(
    (state) => state?.modals
  );
  const { supportCourse, loading } = useSelector(
    (state) => state?.supportCourses
  );

  const open = isModalOpen && modalType === "edit_support_course";
  const supportCourseId = modalProps?.supportCourseId;

  // Charger les données du cours de soutien
  useEffect(() => {
    if (open && supportCourseId) {
      dispatch(getSupportCourse(supportCourseId));
    }
  }, [dispatch, open, supportCourseId]);

  // Mettre à jour le formulaire quand les données sont chargées
  useEffect(() => {
    if (supportCourse) {
      const formValues = {
        instructorId: supportCourse?.instructor?._id,
        subjectId: supportCourse?.subject?._id,
        description: supportCourse?.description,
        price: supportCourse?.price,
        individualPrice: supportCourse?.individualPrice, // Set individualPrice
        educationLevel: supportCourse?.educationLevel,
        type: supportCourse?.type,
        state: supportCourse?.state,
        wilaya: supportCourse?.wilaya,
        coverImg: supportCourse?.coverImage
          ? [
              {
                uid: "-1",
                name: "Image de couverture",
                status: "done",
                url: `${IMAGE_URL}/${supportCourse?.coverImage}`,
              },
            ]
          : [],
        supportCourseRes:
          supportCourse?.ressources?.map((res, index) => ({
            uid: `-${index}`,
            name: `Ressource ${index + 1}`,
            status: "done",
            url: env?.IMAGE_URL + "/" + res?.upload,
          })) || [],
      };
      form?.setFieldsValue(formValues);
    }
  }, [supportCourse, form]);

  const onSubmit = async (values) => {
    try {
      const formData = new FormData();

      // Ajouter les champs de référence
      if (values?.instructorId !== supportCourse?.instructor?._id) {
        formData?.append("instructorId", values?.instructorId);
      }
      if (values?.subjectId !== supportCourse?.subject?._id) {
        formData?.append("subjectId", values?.subjectId);
      }

      // Ajouter les champs non-array uniquement s'ils ont changé
      if (
        JSON?.stringify(values?.price) !== JSON?.stringify(supportCourse?.price)
      ) {
        formData?.append("price", JSON?.stringify(values?.price));
      }

      if (values?.description !== supportCourse?.description) {
        formData?.append("description", values?.description);
      }
      if (values?.educationLevel !== supportCourse?.educationLevel) {
        formData?.append("educationLevel", values?.educationLevel);
      }

      // Ajouter les tableaux
      if (JSON?.stringify(values?.type) !== JSON?.stringify(supportCourse?.type)) {
        formData?.append("type", JSON?.stringify(values?.type));
      }
      if (
        JSON?.stringify(values?.state) !== JSON?.stringify(supportCourse?.state)
      ) {
        formData?.append("state", JSON?.stringify(values?.state));
      }

      // Ajouter l'image de couverture si modifiée
      if (values?.coverImg?.[0]?.originFileObj) {
        formData?.append("coverImg", values?.coverImg[0]?.originFileObj);
      }

      // Gérer les ressources
      const existingResources = supportCourse?.ressources || [];
      const newResources =
        values?.supportCourseRes?.filter((file) => file?.originFileObj) || [];

      // Ajouter les nouvelles ressources
      newResources?.forEach((file) => {
        formData?.append("supportCourseRes", file?.originFileObj);
      });

      // Ajouter les ressources existantes à conserver
      const resourcesToKeep = values?.supportCourseRes
        ?.filter((file) => !file?.originFileObj)?.map((file) => ({ upload: file?.url }));

      if (resourcesToKeep?.length > 0) {
        formData?.append("ressources", JSON?.stringify(resourcesToKeep));
      }

      await dispatch(
        updateSupportCourse({
          id: supportCourseId,
          data: formData,
        })
      )?.unwrap();

      await dispatch(getSupportCourses())?.unwrap();

      notification?.success({
        message: t("supportCourse.crud.form.update.success"),
        duration: 3,
      });
      onClose();
    } catch (error) {
      notification?.error({
        message: t("supportCourse.crud.form.update.error"),
        description: error?.message || t("common.error.generic"),
        duration: 4,
      });
    }
  };

  const onClose = () => {
    form?.resetFields();
    dispatch(closeModal());
  };

  return (
    <Modal
      title={t("supportCourse.crud.edit")}
      open={open}
      onCancel={onClose}
      onOk={() => form?.submit()}
      confirmLoading={loading}
      width={800}
      destroyOnClose
    >
      <SupportCourseForm
        form={form}
        onSubmit={onSubmit}
        initialValues={supportCourse}
        mode="edit"
      />
    </Modal>
  );
};

export default EditSupportCourseModal;
